import 'zone.js';
import '@angular/compiler';
import "./index.scss";
import { ApiInterface, SceneLayerInterface, TransitionStartEventInterface, ViewType, getApi } from "@navvis/ivion";
import { DefectContextMenuLayer, DefectPanoramaContextMenuLayer } from "./app/defects/defect-context-menu.layer";
import { keepOnlyContextMenuLayers } from "./app/api/ivion-context-menu";
import { PoiSelectedEventData } from "./PoiSelectedEventData";
import { bootstrapApplication } from '@angular/platform-browser';
import { ApplicationRef } from '@angular/core';
import { AppComponent } from './app/app.component';
import { createAppConfig } from './app/app.config';
import { PoiDetailService } from './app/poi-detail.service';
import { NavvisTokenService } from './app/api/navvis-token.service';
import { TokenRefreshService } from './app/api/token-refresh.service';
import { DefectEventService } from './app/defects/defect-event.service';
import { TranslationService } from './app/services/translation.service';
import { ToastService } from './app/services/toast.service';
import { getConfig } from './app/config.service';

class IvionApp {
	public ivApi: ApiInterface | null = null;
	private navvisTokenService: NavvisTokenService | null = null;
	private poiDetailService: PoiDetailService | null = null;
	private defectEventService: DefectEventService | null = null;
	private translationService: TranslationService | null = null;
	private toastService: ToastService | null = null;
	private appRef: ApplicationRef | null = null;
	private initialized = false;
	private currentSiteId: number | null = null;
	/** Our context menu layers; every other (built-in) entry is removed after site load. */
	private contextMenuLayers: SceneLayerInterface[] = [];

	/**
	 * Stores Angular application reference for later use.
	 */
	setAppRef(appRef: ApplicationRef): void {
		this.appRef = appRef;
		this.navvisTokenService = appRef.injector.get(NavvisTokenService);
		this.poiDetailService = appRef.injector.get(PoiDetailService);
		this.defectEventService = appRef.injector.get(DefectEventService);
		this.translationService = appRef.injector.get(TranslationService);
		this.toastService = appRef.injector.get(ToastService);
	}

	/**
	 * Initializes the IVION viewer with a specific site.
	 * Called when user selects a site from the site selector.
	 */
	async initializeWithSite(navvisSiteId: number): Promise<void> {
		if (!this.navvisTokenService) {
			console.error('NavvisTokenService not available. Call setAppRef first.');
			return;
		}

		// If already initialized, skip re-initialization
		// Note: IVION SDK doesn't expose loadedSite, so we track it ourselves
		if (this.initialized && this.ivApi && this.currentSiteId === navvisSiteId) {
			console.log('[IvionApp] Already initialized with site:', navvisSiteId);
			return;
		}

		try {
			// Get IVION token from middleware (requires authentication)
			const loginToken = await this.navvisTokenService.getIvionTokenAsync();

			// Initialize IVION API if not already done
			if (!this.ivApi) {
				this.ivApi = await getApi(getConfig().api.navvisUrl + "/", {
					loginToken: loginToken
				});

				// Context menu: "Report defect" on the 2D map and in the panorama.
				// These are the only entries the app offers (see keepOnlyContextMenuLayers below).
				if (this.defectEventService && this.translationService) {
					this.contextMenuLayers = [
						new DefectContextMenuLayer(
							this.ivApi.view.mapView,
							this.ivApi,
							this.defectEventService,
							this.translationService
						),
						new DefectPanoramaContextMenuLayer(
							this.ivApi.view.mainView,
							this.defectEventService,
							this.translationService
						),
					];
				}

				// Connect POI click events to Angular: emit the POI to the Angular
				// side so the info panel opens, then return `false` to let the SDK's
				// default action proceed (panorama camera navigates to the clicked
				// POI -- the zoom-to-POI UX users expect).
				//
				// The follow-up navigation triggers a known SDK bug on large sites
				// where the map's POI layer filters every other POI out by distance.
				// We work around that by locking `distanceThreshold` to Infinity
				// after `loadSite()` below.
				//
				// Per SDK docs (@navvis/ivion api.d.ts PoiClickHandler):
				//   Return `true` to consume the event and prevent the default action.
				//   Return `false` to let the default action proceed.
				this.ivApi.poi.service.onPoiClick.connect(poi => {
					const data = new PoiSelectedEventData();
					data.globalLocation = poi.location;
					data.icon = poi.icon;
					data.id = poi.id;
					data.poiTypeId = poi.poiType.id;
					data.titles = poi.titles;
					data.descriptions = poi.descriptions;

					// Send POI data to Angular app (via DI service with NgZone)
					this.poiDetailService!.emitPoiSelected(data);

					// Unhighlight previously highlighted POIs (NavVis doesn't do it automatically).
					this.ivApi!.poi.service.unhighlightPois(
						Array.from(this.ivApi!.poi.service.highlightedPois).filter(p => p.id !== poi.id)
					);

					return false;
				});

				// Legacy app.bootstrapp.ts: react to transitions to locations without a panorama.
				this.ivApi.view.service.onTransitionStart.connect(event => this.handlePanoramaView(event));
			}

			// Load the selected site
			const site = await this.ivApi.site.repository.findOne(navvisSiteId);
			await this.ivApi.site.service.loadSite(site);

			// Same view setup as the legacy app (app.bootstrapp.ts): no secondary view
			// window and no automatic point cloud when there is no panorama. Without
			// the latter IVION rejects goToPoi / moveToGeoLocation for locations with
			// no reachable image ("Location is farther than 100m away from any
			// dataset"); with it disabled IVION just moves the map there.
			this.ivApi.view.service.showSecondaryView = false;
			this.ivApi.view.service.allowPointCloudActivation = false;

			// IVION 11.9.0 does not default to "all POIs visible" the way the legacy
			// @navvis/indoorviewer SDK did. Omitting the types arg affects ALL POI types.
			this.ivApi.legacyApi.setPoiTypesVisibility(true);

			// Remove the IVION built-in context menu entries (route to/from here, ...)
			// so only "Report defect" is offered.
			keepOnlyContextMenuLayers(this.ivApi.view.mapView, this.contextMenuLayers);
			keepOnlyContextMenuLayers(this.ivApi.view.mainView, this.contextMenuLayers);

			// Workaround for SDK distance-filter bug: the map's POI layer filters
			// POIs by 3D distance from the mainView (panorama) camera, using a
			// hard-coded default threshold of 2000m. After a POI click the SDK
			// navigates the panorama to that POI's location; POIs more than 2000m
			// from the new panorama position then disappear from the map. There
			// is no SDK-level setting to disable this, so we lock the threshold
			// to Infinity via a non-writable property descriptor. Safe across
			// sites: on small ones the threshold never mattered anyway.
			const poiLayerParams = (this.ivApi.view.mapView as any).getPoiLayer()?.params;
			if (poiLayerParams && Object.getOwnPropertyDescriptor(poiLayerParams, 'distanceThreshold')?.configurable !== false) {
				delete poiLayerParams.distanceThreshold;
				Object.defineProperty(poiLayerParams, 'distanceThreshold', {
					get: () => Infinity,
					set: () => { /* ignore SDK resets */ },
					configurable: false,
				});
			}

			this.initialized = true;
			this.currentSiteId = navvisSiteId;
			console.log('[IvionApp] Initialized with site:', navvisSiteId);
		} catch (error) {
			console.error('[IvionApp] Failed to initialize:', error);
			throw error;
		}
	}

	/**
	 * Legacy app.bootstrapp.ts handlerPanoramaView: when a transition targets a
	 * location without a panorama image, hide the secondary view, tell the user
	 * and make sure the map is the primary view.
	 */
	private handlePanoramaView(event: TransitionStartEventInterface | undefined): void {
		const viewService = this.ivApi?.view.service;
		if (!viewService) {
			return;
		}
		if (!event) {
			console.info('[IvionApp] onTransitionStart: empty event');
			return;
		}
		console.info('[IvionApp] onTransitionStart, image:', event.image?.id);

		if (viewService.getPrimaryView() === ViewType.MAP) {
			if (event.image === undefined) {
				viewService.showSecondaryView = false;
				this.showPanoramaNotFound();
			} else {
				viewService.showSecondaryView = true;
			}
		} else if (event.image === undefined) {
			this.showPanoramaNotFound();
			viewService.showSecondaryView = false;
			viewService.swapViews();
		}
	}

	private showPanoramaNotFound(): void {
		const fallback = 'Nemáte oprávnění k panorama View této budovy.';
		this.toastService?.warning(this.translationService?.translate('map_alert_panorama_not_found', fallback) ?? fallback);
	}

	/**
	 * Checks if IVION is initialized.
	 */
	isInitialized(): boolean {
		return this.initialized && this.ivApi !== null;
	}
}

// Global app instance for Angular ↔ IVION communication
const ivionApp = new IvionApp();
(window as any).IvionApp = ivionApp;
// Keep backwards compatibility with old name
(window as any).TestApp = ivionApp;

// Cookie consent configuration
// 'essential' = accept only essential cookies (UC_UI.denyAllConsents)
// 'all' = accept all cookies including marketing/functional (UC_UI.acceptAllConsents)
type CookieConsentMode = 'essential' | 'all';
const COOKIE_CONSENT_MODE: CookieConsentMode = 'essential'; // nebo 'all'

// Auto-dismiss Usercentrics cookie consent dialog
// This is an enterprise app with mandatory login, so we skip the consent UI
// Uses MutationObserver because UC_UI loads asynchronously when IVION loads a site
const autoDismissCookieConsent = (mode: CookieConsentMode) => {
	const dismissConsent = () => {
		const UC_UI = (window as any).UC_UI;
		if (UC_UI?.isInitialized?.() && UC_UI.isConsentRequired?.()) {
			if (mode === 'all') {
				UC_UI.acceptAllConsents();
				console.log('[Cookies] Auto-accepted all cookies');
			} else {
				UC_UI.denyAllConsents();
				console.log('[Cookies] Auto-accepted essential cookies only');
			}
			UC_UI.closeCMP();
			return true;
		}
		return false;
	};

	// Watch for Usercentrics root element to appear in DOM
	const observer = new MutationObserver((_, obs) => {
		const ucRoot = document.getElementById('usercentrics-root');
		if (ucRoot) {
			// Element found, poll for UC_UI to be ready
			const pollInterval = setInterval(() => {
				if (dismissConsent()) {
					clearInterval(pollInterval);
					obs.disconnect();
				}
			}, 100);

			// Safety timeout - stop polling after 5 seconds
			setTimeout(() => {
				clearInterval(pollInterval);
				obs.disconnect();
			}, 5000);
		}
	});

	observer.observe(document.body, { childList: true, subtree: true });
};

autoDismissCookieConsent(COOKIE_CONSENT_MODE);

// Auto-dismiss IVION "What's new?" changelog dialog
// Uses MutationObserver to detect and close the dialog when it appears
const autoDismissChangelogDialog = () => {
	const observer = new MutationObserver(() => {
		const dialog = document.querySelector('.full-height-modal-no-margin');
		if (dialog) {
			const closeButton = dialog.querySelector('.close-button') as HTMLElement;
			if (closeButton) {
				closeButton.click();
				console.log('[Changelog] Auto-dismissed changelog dialog');
				observer.disconnect();
			}
		}
	});

	observer.observe(document.body, { childList: true, subtree: true });
};

autoDismissChangelogDialog();

// Load runtime config, then bootstrap Angular application
import { loadConfig } from './app/config.service';

loadConfig()
	.then(() => bootstrapApplication(AppComponent, createAppConfig()))
	.then((appRef: ApplicationRef) => {
		console.log('Angular bootstrapped successfully');

		// Store app reference for later IVION initialization
		ivionApp.setAppRef(appRef);

		// Initialize IVION token refresh service
		// This subscribes to OIDC token changes and updates IVION SDK
		const tokenRefreshService = appRef.injector.get(TokenRefreshService);
		tokenRefreshService.initialize();

		// IVION will be initialized when user selects a site
		// See site-selector.component.ts and main.component.ts
	})
	.catch(err => console.error('Angular bootstrap error:', err));
